
import * as React from "react";
import { ChevronDown, Check, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";

export interface ComboboxOption {
  label: string;
  value: string;
}

interface ComboboxProps {
  value: string | null;
  onChange: (value: string) => void;
  options?: ComboboxOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean; // ✅ Added disabled prop
}

export const Combobox: React.FC<ComboboxProps> = ({
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  className = "w-1/2",
  disabled = false, // ✅ Default to false
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearchTerm("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Ensure options is always a valid array
  const safeOptions = React.useMemo(() => {
    if (!options || !Array.isArray(options)) return [];
    return options.filter(option => option && option.value && option.label);
  }, [options]);

  // ✅ Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return safeOptions;
    return safeOptions.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.value.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [safeOptions, searchTerm]);

  const selectedOption = safeOptions.find(opt => opt.value === value);

  const handleSelect = (optionValue: string) => {
    if (disabled) return; // ✅ Prevent selection when disabled
    console.log("Selecting:", optionValue); // Debug log
    onChange(optionValue);
    setOpen(false);
    setSearchTerm("");
  };

  const toggleOpen = () => {
    if (disabled) return; // ✅ Prevent opening when disabled
    setOpen(!open);
    if (!open) {
      setSearchTerm("");
    }
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <Button
        ref={buttonRef}
        variant="outline"
        role="combobox"
        aria-expanded={open}
        disabled={disabled} // ✅ Pass disabled to Button
        className={cn(
          "text-[#020617] justify-between mt-2",
          disabled && "opacity-50 cursor-not-allowed", // ✅ Visual styling for disabled state
          className
        )}
        onClick={toggleOpen}
        type="button"
      >
        {selectedOption ? selectedOption.label : placeholder}
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {/* Dropdown - only render if not disabled */}
      {open && !disabled && (
        <div
          ref={dropdownRef}
          className={cn(
            "absolute z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg",
            className
          )}
        >
          {/* Search Input */}
          <div className="flex items-center border-b border-gray-200 px-3 py-2">
            <Search className="mr-2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
              autoFocus
            />
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-center text-sm text-gray-500">
                {searchTerm ? "No results found." : "No options available."}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-accent cursor-pointer",
                    //value === option.value && "bg-accent"
                  )}
                  onClick={() => handleSelect(option.value)}
                >
                  <span>{option.label}</span>
                  <Check
                    className={cn(
                      "h-4 w-4 text-primary",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};